import { useState } from "react";
import { Briefcase, MapPin, Clock, Calendar, ChevronDown, ChevronUp, Sparkles, Send, Award, Heart, Users, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { careers } from "../mock/mock";
import { submitCareer } from "../services/api";
import CareersInfographic from "../components/infographics/CareersInfographic";

const CareersPage = () => {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(null);
  const [applyRole, setApplyRole] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitCareer({ ...form, role: applyRole?.title });
      toast({ title: "Application submitted!", description: "Our HR team will review and reach out soon." });
      setApplyRole(null);
      setForm({ name: "", email: "", phone: "", experience: "", message: "" });
    } catch (err) {
      toast({ title: "Submission failed", description: err.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const values = [
    { icon: Heart, title: "Purpose-Driven", desc: "Shape the future of Indian children through meaningful early education." },
    { icon: Award, title: "Grow Your Craft", desc: "Continuous training in ECCE, Sanskrit, yoga and modern pedagogy." },
    { icon: Users, title: "Warm Culture", desc: "Small, supportive team that celebrates every milestone together." }
  ];

  return (
    <div>
      <section className="pt-16 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Careers</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Join Our <span className="text-orange-600">Founding Team</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            We are hiring passionate educators, caregivers and admin professionals to help launch India's most unique play school on 1st January 2027.
          </p>
        </div>
      </section>

      <CareersInfographic />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-2">
              Open <span className="text-orange-600">Positions</span>
            </h2>
            <p className="text-stone-600">{careers.length} openings across academics, wellness & administration</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {careers.map((job) => (
              <div key={job.id} className="bg-white rounded-2xl border border-orange-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => setExpanded(expanded === job.id ? null : job.id)}
                  className="w-full p-6 flex items-start justify-between gap-4 hover:bg-orange-50/50 transition-colors text-left"
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-orange-700 bg-orange-100 px-2 py-1 rounded-full uppercase tracking-wider">
                        {job.department}
                      </span>
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-full uppercase tracking-wider">
                        {job.type}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-xl text-stone-800 mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-xs text-stone-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location}</span>
                      <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {job.experience}</span>
                    </div>
                  </div>
                  {expanded === job.id ? <ChevronUp className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" /> : <ChevronDown className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />}
                </button>

                {expanded === job.id && (
                  <div className="px-6 pb-6 border-t border-orange-100 pt-6 space-y-5">
                    <p className="text-stone-700 leading-relaxed">{job.description}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-display font-bold text-stone-800 mb-3">Responsibilities</h4>
                        <ul className="space-y-2">
                          {job.responsibilities.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0" />
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-display font-bold text-stone-800 mb-3">Requirements</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <Button
                      onClick={() => setApplyRole(job)}
                      className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full h-11 px-6 btn-shine"
                    >
                      Apply for this Role
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply modal */}
      {applyRole && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setApplyRole(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-orange-700 font-bold">Applying for</p>
                <h3 className="font-display font-bold text-2xl text-stone-800">{applyRole.title}</h3>
              </div>
              <button onClick={() => setApplyRole(null)} className="text-stone-400 hover:text-stone-600 text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-700 font-semibold">Full Name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-orange-50/50 h-11 border-orange-200" />
                </div>
                <div>
                  <Label className="text-stone-700 font-semibold">Phone *</Label>
                  <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 bg-orange-50/50 h-11 border-orange-200" />
                </div>
                <div>
                  <Label className="text-stone-700 font-semibold">Email *</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-orange-50/50 h-11 border-orange-200" />
                </div>
                <div>
                  <Label className="text-stone-700 font-semibold">Experience (Years) *</Label>
                  <Input required value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1.5 bg-orange-50/50 h-11 border-orange-200" />
                </div>
              </div>
              <div>
                <Label className="text-stone-700 font-semibold">Why do you want to join us?</Label>
                <Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-orange-50/50 border-orange-200 min-h-[100px]" placeholder="Tell us a bit about your journey and interest..." />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setApplyRole(null)} className="rounded-full border-stone-300">Cancel</Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full h-11 btn-shine">
                  {loading ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Submitting...</> : <>Submit Application <Send className="ml-2 w-4 h-4" /></>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersPage;
